import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, RefreshCw, Loader, Check, ChevronRight, Lightbulb } from 'lucide-react';
import api from '../api';

const examples = [
  "When someone fills my contact form, save their details to Google Sheets and send a welcome email via Gmail",
  "Every Monday at 9am, send me a summary of this week's Google Calendar events on Telegram",
  "When I receive an email with the word 'invoice', save the attachment to Google Drive and notify me on Telegram",
  "When someone books via Calendly, add them to Google Sheets and send a confirmation email from Gmail",
];

export default function NewAutomation() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [step, setStep] = useState('write');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    setLoading(true); setError(''); setStep('confirming');
    try {
      const res = await api.post('/automations/preview', { description });
      setPreview(res.data); setStep('confirmed');
    } catch {
      setError('Could not process your description. Please try again.');
      setStep('write');
    } finally { setLoading(false); }
  };

  const handleBuild = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post('/automations', { title, description });
      navigate(`/automation/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
      setLoading(false);
    }
  };

  const steps = ['Describe', 'Review', 'Build'];
  const stepIndex = step === 'write' ? 0 : step === 'confirming' || step === 'confirmed' ? 1 : 2;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 24, color: 'var(--gray-500)' }} onClick={() => navigate('/')}>
        <ArrowLeft size={14} /> Back
      </button>

      <h1 style={{ marginBottom: 4 }}>New Automation</h1>
      <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 28 }}>Describe what you want in plain English — we'll build it.</p>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 600,
                background: i < stepIndex ? 'var(--accent)' : i === stepIndex ? 'var(--accent)' : 'var(--gray-200)',
                color: i <= stepIndex ? 'white' : 'var(--gray-500)',
                transition: 'all 0.2s'
              }}>
                {i < stepIndex ? <Check size={11} /> : i + 1}
              </div>
              <span style={{ fontSize: 13, color: i === stepIndex ? 'var(--gray-900)' : 'var(--gray-400)', fontWeight: i === stepIndex ? 500 : 400 }}>{s}</span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: i < stepIndex ? 'var(--accent)' : 'var(--border)', margin: '0 12px', transition: 'background 0.3s' }} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 */}
      {(step === 'write' || step === 'confirming') && (
        <div className="card" style={{ marginBottom: 20 }}>
          <form onSubmit={handlePreview}>
            <div style={{ marginBottom: 16 }}>
              <label>Automation name</label>
              <input placeholder="e.g. New lead to Google Sheets" value={title}
                onChange={e => setTitle(e.target.value)} required autoFocus />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label>What should it do?</label>
              <textarea rows={5}
                placeholder="Describe it naturally — like explaining to an assistant. Include what triggers it, what platforms are involved, and what should happen."
                value={description} onChange={e => setDescription(e.target.value)} required />
              <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 5 }}>The more detail you give, the better the result.</p>
            </div>
            {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14, padding: '9px 12px', background: 'var(--red-light)', borderRadius: 7 }}>{error}</div>}
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
              disabled={loading || !title.trim() || !description.trim()}>
              {loading
                ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analysing...</>
                : <>Continue <ChevronRight size={14} /></>}
            </button>
          </form>
        </div>
      )}

      {/* Step 2 — Review */}
      {step === 'confirmed' && preview && (
        <div className="card" style={{ marginBottom: 20, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, background: 'var(--accent-light)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Check size={14} color="var(--accent)" />
            </div>
            <div>
              <h2 style={{ fontSize: 15 }}>Review your automation</h2>
              <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Confirm this is what you want before we build it</p>
            </div>
          </div>

          <div style={{ padding: '14px 16px', background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 16, fontSize: 14, lineHeight: 1.7, color: 'var(--gray-700)' }}>
            {preview.summary}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Trigger</p>
              <p style={{ fontSize: 13, color: 'var(--gray-700)', padding: '9px 12px', background: 'var(--gray-50)', borderRadius: 7, border: '1px solid var(--border)' }}>{preview.trigger}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Platforms</p>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {preview.platforms?.map(p => (
                  <span key={p} style={{ fontSize: 12, padding: '4px 9px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 20, fontWeight: 500 }}>{p}</span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Actions</p>
            {preview.actions?.map((action, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--gray-100)', color: 'var(--gray-500)', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.5 }}>{action}</p>
              </div>
            ))}
          </div>

          {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14, padding: '9px 12px', background: 'var(--red-light)', borderRadius: 7 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { setStep('write'); setPreview(null); }}>
              <RefreshCw size={13} /> Edit
            </button>
            <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center', padding: '10px' }} onClick={handleBuild} disabled={loading}>
              {loading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Building...</> : <><Zap size={14} /> Build this automation</>}
            </button>
          </div>
        </div>
      )}

      {/* Examples */}
      {step === 'write' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <Lightbulb size={13} color="var(--gray-400)" />
            <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>Try one of these examples</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {examples.map((ex, i) => (
              <button key={i} onClick={() => setDescription(ex)}
                style={{
                  padding: '11px 14px', background: 'var(--white)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  fontSize: 13, color: 'var(--gray-600)', cursor: 'pointer',
                  textAlign: 'left', transition: 'all 0.15s', fontFamily: 'DM Sans'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--gray-900)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--gray-600)'; }}>
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
